import { useEffect, useState, useCallback } from 'react';
import { Select, Button, Input, Typography, Checkbox, Radio, App } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { loadFieldMetas } from '../../../contract/linkedMeta';
import type { FieldMeta } from '../../../api/types';
import type { DetailTableSection, DetailColumn } from '../../../contract/types';
import { genId } from '../../../contract/types';

const LINK_TYPES = [18, 21];
/** 开汇总行时默认勾成求和列的字段类型：数字 / 货币 / 公式 / 查找引用 / 进度。 */
const NUMERIC_TYPES = [2, 99003, 20, 19, 99002];

interface Props {
  section: DetailTableSection;
  onChange: (s: DetailTableSection) => void;
  fieldMetas: FieldMeta[];
}

export function DetailTableEditor({ section, onChange, fieldMetas }: Props) {
  const { message } = App.useApp();
  const linkFields = fieldMetas.filter((m) => LINK_TYPES.includes(m.type));
  const [linkedFieldMetas, setLinkedFieldMetas] = useState<FieldMeta[]>([]);
  const [loadingLinked, setLoadingLinked] = useState(false);

  const loadLinkedTable = useCallback(async (linkFieldId: string) => {
    const meta = fieldMetas.find((m) => m.id === linkFieldId);
    if (!meta) return;
    const prop = (meta as unknown as { property?: { tableId?: string } }).property;
    const linkedTableId = prop?.tableId;
    if (!linkedTableId) return;

    setLoadingLinked(true);
    try {
      const metas = await loadFieldMetas(linkedTableId);
      setLinkedFieldMetas(metas);
      // 选的还是同一个关联字段（比如只是重新点了一下下拉），不能把用户
      // 配好的列、排序、筛选、汇总全部推倒重来
      const sameField = section.linkFieldId === linkFieldId && section.linkedTableId === linkedTableId;
      if (sameField) return;
      onChange({
        ...section,
        linkFieldId,
        linkedTableId,
        columns: metas.slice(0, 5).map((m) => ({
          id: genId(),
          fieldId: m.id,
          label: m.name,
          width: null,
          align: 'left' as const,
        })),
        // 旧列没了，指向旧列 id 的排序/筛选/求和列一并作废，
        // 否则界面上显示成一串 id、打印时又静默不生效
        sort: null,
        filter: null,
        summaryRow: section.summaryRow ? { ...section.summaryRow, sumColumns: [] } : null,
      });
    } catch (e) {
      setLinkedFieldMetas([]);
      message.error(`读取关联表字段失败：${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setLoadingLinked(false);
    }
  }, [fieldMetas, onChange, section, message]);

  useEffect(() => {
    if (!section.linkFieldId) return;
    // 以字段当前指向的表为准，而不是模板里存的那个：用户在 Base 里把关联字段
    // 改指向另一张表后，存下来的 linkedTableId 就过期了 —— 打印用的是实时值，
    // 编辑器却还在列旧表的字段，两边对不上。
    const live = (
      fieldMetas.find((m) => m.id === section.linkFieldId) as unknown as {
        property?: { tableId?: string };
      } | undefined
    )?.property?.tableId;
    const tableId = live || section.linkedTableId;
    if (!tableId) return;
    loadFieldMetas(tableId)
      .then(setLinkedFieldMetas)
      .catch(() => setLinkedFieldMetas([]));
  }, [section.linkedTableId, section.linkFieldId, fieldMetas]);

  const updateColumn = (id: string, patch: Partial<DetailColumn>) => {
    const columns = section.columns.map((c) => {
      if (c.id !== id) return c;
      const next = { ...c, ...patch };
      // 换了字段但表头还写着旧字段名 —— 除非用户自己改过标题，否则跟着更新
      if (patch.fieldId && patch.fieldId !== c.fieldId && patch.label === undefined) {
        const oldName = linkedFieldMetas.find((m) => m.id === c.fieldId)?.name;
        const newName = linkedFieldMetas.find((m) => m.id === patch.fieldId)?.name;
        if (newName && (!c.label || c.label === oldName)) next.label = newName;
      }
      return next;
    });
    onChange({ ...section, columns });
  };

  const removeColumn = (id: string) => {
    const columns = section.columns.filter((c) => c.id !== id);
    // 排序/筛选/求和列都是按列 id 引用的，删了列不清理的话，
    // 下拉框里会显示一串裸 id，打印时又悄悄不生效
    onChange({
      ...section,
      columns,
      sort: section.sort?.columnId === id ? null : section.sort,
      filter: section.filter?.columnId === id ? null : section.filter,
      summaryRow: section.summaryRow
        ? { ...section.summaryRow, sumColumns: section.summaryRow.sumColumns.filter((c) => c !== id) }
        : null,
    });
  };

  const addColumn = (fieldId: string) => {
    const meta = linkedFieldMetas.find((m) => m.id === fieldId);
    if (!meta) return;
    const col: DetailColumn = {
      id: genId(),
      fieldId,
      label: meta.name,
      width: null,
      align: 'left',
    };
    onChange({ ...section, columns: [...section.columns, col] });
  };

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>关联字段</Typography.Text>
        <Select
          size="small"
          style={{ width: '100%', marginTop: 2 }}
          placeholder="选择关联字段（SingleLink / DuplexLink）"
          value={section.linkFieldId || undefined}
          onChange={(v) => void loadLinkedTable(v)}
          options={linkFields.map((m) => ({ label: m.name, value: m.id }))}
          loading={loadingLinked}
        />
      </div>

      {linkedFieldMetas.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>列配置</Typography.Text>
          {section.columns.map((col) => (
            <div key={col.id} style={{ display: 'flex', gap: 4, alignItems: 'center', marginBottom: 4 }}>
              <Input
                size="small"
                style={{ width: 80 }}
                value={col.label}
                onChange={(e) => updateColumn(col.id, { label: e.target.value })}
                placeholder="列名"
              />
              <Select
                size="small"
                style={{ flex: 1, minWidth: 0 }}
                value={col.fieldId}
                onChange={(v) => updateColumn(col.id, { fieldId: v })}
                options={linkedFieldMetas.map((m) => ({ label: m.name, value: m.id }))}
              />
              <Radio.Group
                size="small"
                value={col.align}
                onChange={(e) => updateColumn(col.id, { align: e.target.value })}
                optionType="button"
                options={[
                  { label: 'L', value: 'left' },
                  { label: 'C', value: 'center' },
                  { label: 'R', value: 'right' },
                ]}
              />
              <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeColumn(col.id)} />
            </div>
          ))}
          {linkedFieldMetas.length > 0 && (
            <Select
              size="small"
              style={{ width: 160, marginTop: 4 }}
              placeholder="+ 添加列"
              value={undefined}
              onChange={(v) => { if (v) addColumn(v); }}
              options={linkedFieldMetas
                .filter((m) => !section.columns.some((c) => c.fieldId === m.id))
                .map((m) => ({ label: m.name, value: m.id }))}
            />
          )}
        </div>
      )}

      {section.columns.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>排序</Typography.Text>
          <Select
            size="small"
            style={{ width: 120 }}
            allowClear
            placeholder="不排序"
            value={section.sort?.columnId}
            onChange={(columnId) =>
              onChange({ ...section, sort: columnId ? { columnId, dir: section.sort?.dir || 'asc' } : null })
            }
            options={section.columns.map((c) => ({ label: c.label, value: c.id }))}
          />
          {section.sort?.columnId && (
            <Radio.Group
              size="small"
              value={section.sort.dir}
              onChange={(e) => onChange({ ...section, sort: { columnId: section.sort!.columnId, dir: e.target.value } })}
              optionType="button"
              options={[
                { label: '升序', value: 'asc' },
                { label: '降序', value: 'desc' },
              ]}
            />
          )}
        </div>
      )}

      {section.columns.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>筛选</Typography.Text>
          <Select
            size="small"
            style={{ width: 100 }}
            allowClear
            placeholder="不筛选"
            value={section.filter?.columnId}
            onChange={(columnId) =>
              onChange({
                ...section,
                filter: columnId ? { columnId, op: section.filter?.op || 'eq', value: section.filter?.value || '' } : null,
              })
            }
            options={section.columns.map((c) => ({ label: c.label, value: c.id }))}
          />
          {section.filter?.columnId && (
            <>
              <Select
                size="small"
                style={{ width: 76 }}
                value={section.filter.op}
                onChange={(op) => onChange({ ...section, filter: { ...section.filter!, op } })}
                options={[
                  { label: '等于', value: 'eq' },
                  { label: '不等于', value: 'ne' },
                  { label: '包含', value: 'contains' },
                  { label: '大于', value: 'gt' },
                  { label: '小于', value: 'lt' },
                ]}
              />
              <Input
                size="small"
                style={{ width: 90 }}
                placeholder="值"
                value={section.filter.value}
                onChange={(e) => onChange({ ...section, filter: { ...section.filter!, value: e.target.value } })}
              />
            </>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16 }}>
        <Checkbox
          checked={section.showIndex}
          onChange={(e) => onChange({ ...section, showIndex: e.target.checked })}
        >
          <Typography.Text style={{ fontSize: 12 }}>序号列</Typography.Text>
        </Checkbox>
        <Checkbox
          checked={!!section.summaryRow}
          onChange={(e) => {
            if (!e.target.checked) {
              onChange({ ...section, summaryRow: null });
              return;
            }
            // 默认把数值类型的列全勾成求和列 —— 以前是空的，用户开了汇总行
            // 却看到整行空白，还找不到地方选列。
            const numericCols = section.columns
              .filter((c) => NUMERIC_TYPES.includes(linkedFieldMetas.find((m) => m.id === c.fieldId)?.type ?? -1))
              .map((c) => c.id);
            onChange({ ...section, summaryRow: { label: '合计', sumColumns: numericCols } });
          }}
        >
          <Typography.Text style={{ fontSize: 12 }}>汇总行</Typography.Text>
        </Checkbox>
      </div>

      {section.summaryRow && section.columns.length > 0 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>汇总</Typography.Text>
          <Input
            size="small"
            style={{ width: 80 }}
            placeholder="合计"
            value={section.summaryRow.label}
            onChange={(e) => onChange({ ...section, summaryRow: { ...section.summaryRow!, label: e.target.value } })}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>求和列</Typography.Text>
          <Select
            size="small"
            mode="multiple"
            style={{ minWidth: 160, flex: 1 }}
            placeholder="选择要求和的列"
            value={section.summaryRow.sumColumns.filter((id) => section.columns.some((c) => c.id === id))}
            onChange={(sumColumns: string[]) =>
              onChange({ ...section, summaryRow: { ...section.summaryRow!, sumColumns } })
            }
            options={section.columns.map((c) => ({ label: c.label, value: c.id }))}
          />
        </div>
      )}
    </div>
  );
}
